<template>
  <div class="facet-container user-select-none">
    <e-c-facet-header v-if="header" :title="header" :tooltip="toolTipTitle" />
    <div>
      <e-c-select-display
        placeholder="- Any -"
        :showOptions="showOptions"
        :away="away"
        :selectedItems="selectedItems"
        :fieldId="fieldId"
      />
      <div v-if="open" v-click-away="away" class="dropdown">
        <input
          v-if="displayFilterInputBox"
          type="text"
          class="ecl-text-input col"
          :placeholder="filterPlaceholder"
          v-model="filter"
        />

        <!-- SELECT ALL (not necessary for now) -->
        <!-- <label class="custom-checkbox ml-3 mt-3">
          <input type="checkbox" v-model="selectAllChecked" @change="selectAll" label="Select All"
            class="ml-3 mt-3 select-all-checkbox" :disabled="isCheckboxDisabled" />
          <span class="checkmark"></span>
          <span class="label">Select all</span>
        </label> -->
        <div
          class="dropdown-options"
          :class="{ 'border-top': displayFilterInputBox }"
        >
          <!-- Loop through filtered items and display highlighted text -->
          <div
            v-for="(item, index) in filteredItems"
            :key="getTitle(item) + index"
            class="select-item"
          >
            <e-c-checkbox
              :id="`${fieldId}_${itemTitles[index]}`"
              :label="formatFacetLabel(itemTitles[index])"
              :label-right="item.count.toLocaleString('fi')"
              :checked="selectAllChecked || facetIsSelected(fieldId, item)"
              @change="() => facetClicked(fieldId, item)"
            />
            <!-- <span v-html="item.highlightedText"></span>  -->
            <!-- Render highlighted text -->
          </div>
        </div>
        <div class="no-results" v-if="filteredItems.length === 0">
          No results found
        </div>
        <!-- Button group (not nceessary for now)-->
        <!-- <div v-if="openButtons" class="button-group">
          <button @click="applySelection" class="btn ecl-button--primary">Apply</button>
          <button @click="clearAll" class="btn ecl-button--secondary">Clear all</button>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
import { helpers } from "@piveau/piveau-hub-ui-modules";
import { mapGetters } from "vuex";
const { getFacetTranslation } = helpers;
import ECCheckbox from "@/components/ECCheckbox";
import ECFacetHeader from "@/components/ECFacetHeader";
import ECSelectDisplay from "@/components/ec-multiselect/ECSelectDisplay";
import axios from "axios";

let countryVocabularyCache = null;
let countryVocabularyPromise = null;

export default {
  name: "ECMultiSelectFacet",
  components: { ECSelectDisplay, ECFacetHeader, ECCheckbox },
  props: {
    header: {
      type: String,
      default: "",
    },
    fieldId: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      required: true,
    },
    toolTipTitle: {
      type: String,
      default: "",
    },
    showFilter: String,
    facetIsSelected: Function,
    facetClicked: Function,
    setRouteQuery: Function,
  },
  data() {
    return {
      open: false,
      id: null,
      filter: "",
      filterPlaceholder: "Filter",
      filterTimeout: null,
      selectedCatalog: "",
      selectedItems: [],
      openButtons: false,
      selectAllChecked: false,
      langList: [],
      allowedDataScopes: ["_RESEARCH", "_NATIONAL", "CB_EU", "CB_OTHER"],
    };
  },
  computed: {
    ...mapGetters("datasets", ["getFacets", "getAvailableFacets"]),

    myId() {
      // Use Vue generated uid to set give each facet a unique id
      return `facet-${this.id}`;
    },
    itemTitles() {
      return this.filteredItems.map(this.getTitle);
    },
    selection() {
      const selectedItems = this.items.filter((item) =>
        this.facetIsSelected(this.fieldId, item)
      );

      return selectedItems.map((item) => this.getTitle(item)).join(", ");

      // return this.selectedItems.map((item) => this.getTitle(item)).join(", ");
    },
    displayFilterInputBox() {
      const show = (this.showFilter || "").toLowerCase();
      if (show === "never") return false;
      const count = this.items.length;
      return show === "always" || count > 10;
    },
    filteredItems() {
      const lcFilter = this.filter.toLowerCase();
      const result = this.items.filter((item) =>
        this.getTitle(item).toLowerCase().includes(lcFilter)
      );
      if (this.fieldId === "corporate-body-classification") {
        return result.filter((item) =>
          this.allowedDataScopes.includes(item.id)
        );
      }
      return result;
    },
    highlightedItems() {
      const lcFilter = this.filter.toLowerCase();
      return this.items
        .filter((item) => this.getTitle(item).toLowerCase().includes(lcFilter))
        .map((item) => {
          // Highlight matching text by wrapping in <b> tags
          const title = this.getTitle(item);
          const highlightedText = lcFilter
            ? title.replace(new RegExp(`(${lcFilter})`, "gi"), "<b>$1</b>")
            : title;
          return {
            ...item,
            highlightedText,
          };
        });
    },
    isCheckboxDisabled() {
      return this.filter.length > 0;
    },
  },
  methods: {
    async loadLangList() {
      if (this.fieldId !== "country") {
        this.langList = [];
        return;
      }

      if (Array.isArray(countryVocabularyCache)) {
        this.langList = countryVocabularyCache;
        return;
      }

      if (countryVocabularyPromise) {
        this.langList = await countryVocabularyPromise;
        return;
      }

      try {
        const baseUrl = this.$env?.api?.baseUrl || "";
        const url = new URL(
          "search?filter=vocabulary&vocabulary=country&limit=500",
          baseUrl
        ).toString();
        countryVocabularyPromise = axios
          .get(url)
          .then((response) => response?.data?.result?.results || []);

        const results = await countryVocabularyPromise;
        countryVocabularyCache = results;
        this.langList = results;
      } catch (e) {
        console.error("Fehler beim Laden der Sprachliste:", e);
        this.langList = [];
      } finally {
        countryVocabularyPromise = null;
      }
    },
    getFacetTranslation,
    formatFacetLabel(label) {
      if (!label) return "";
      const map = {
        use_cases: "Use Cases",
        factsheets_reports: "Factsheets reports",
        country_insight: "Country insight",
        covid_19_data_stories: "Covid-19 data stories",
        data_stories: "Data stories",
        lesson: "Academy lesson",
        course: "Academy course",
        faq: "FAQ"
      };
      if (map[label]) return map[label];
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    getTitle(item) {
      const fallbackTitle = this.resolveLocalizedTitle(item?.title);
      return this.fieldId === "dataScope" &&
        this.i18n.global.te(
          `message.datasetFacets.facets.datascopeField.${item.id}`
        )
        ? this.i18n.global.t(
            `message.datasetFacets.facets.datascopeField.${item.id}`
          )
        : this.getFacetTranslationWrapper(
            this.fieldId,
            item.id,
            this.$route.query.locale,
            fallbackTitle || item.title
          );
    },
    resolveLocalizedTitle(title) {
      if (!title || typeof title !== "object") return title;
      const titleMap =
        title.title && typeof title.title === "object" ? title.title : title;
      const locale = this.$route.query.locale || this.$i18n.locale || "en";
      return (
        titleMap[locale] ||
        titleMap.en ||
        Object.values(titleMap).find((value) => typeof value === "string") ||
        ""
      );
    },
    getFacetTranslationWrapper(fieldId, facetId, userLocale, fallback) {
      if (fieldId === "country") {
        if (String(facetId).toLowerCase() === "eu") {
          return "Europe";
        }
        fieldId = this.getFacetTranslation(fieldId, facetId, userLocale, fallback);
        const locale = userLocale === "no" ? "nb" : userLocale;
        const item = this.langList.find((obj) => obj.pref_label.en === fallback);
        const name = item && item.pref_label && item.pref_label[locale];

        // console.log(item, fallback);

        return name || fallback;
      } else {
        return fieldId === "scoring"
          ? this.i18n.global.t(
              `message.datasetFacets.facets.scoring.${facetId}`
            )
          : this.getFacetTranslation(fieldId, facetId, userLocale, fallback);
      }
    },
    away() {
      this.open = false;
    },
    showOptions() {
      this.open = true;
    },
    isSelected(item) {
      return this.selectedItems.includes(item);
    },
    // Toggle selection of an item
    toggleSelection(item) {
      this.openButtons = true;
      const fieldId = this.fieldId;

      if (this.isSelected(item)) {
        // Remove item from selectedItems and update URL
        this.selectedItems = this.selectedItems.filter(
          (selected) => selected !== item
        );
      } else {
        // Add item to selectedItems and update URL
        this.selectedItems.push(item);
      }

      // Update URL to reflect the current selections
      this.setRouteQuery(
        { [fieldId]: this.selectedItems.map((i) => i.id), page: 1 },
        "replace"
      );
    },
    // Select all items
    selectAll() {
      if (this.selectAllChecked) {
        this.selectedItems = [...this.filteredItems];
      } else {
        this.selectedItems = [];
      }

      // Update URL for all selections
      this.setRouteQuery(
        { [this.fieldId]: this.selectedItems.map((i) => i.id), page: 1 },
        "replace"
      );
      this.openButtons = true;
    },
    // setRouteQuery(query, mode) {
    //   if (mode === "replace") {
    //     this.$router.replace(
    //       { query: Object.assign({}, this.$route.query, query) }
    //     ).catch(
    //       error => { console.error(error); }
    //     );
    //   } else {
    //     this.$router.push(
    //       { query: Object.assign({}, this.$route.query, query) }
    //     ).catch(
    //       error => { console.error(error); }
    //     );
    //   }
    // },
    // Clear all selections
    clearAll() {
      this.selectedItems = [];
      this.filteredItems = [];
      this.filter = "";
      // clear also the checkbox!!!
      this.openButtons = false;
    },
    // Apply selection (emit to parent or perform specific action)
    applySelection() {
      console.log(
        "Applying selection with selected items:",
        this.selectedItems
      );
      // Iterate over each selected item and call facetClicked
      // this.selectedItems.forEach((item) => {
      //   this.facetClicked(this.fieldId, item);
      // });
      this.open = false; // Close dropdown after applying
    },
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler(newQuery) {
        // Initialize selectedItems based on URL query parameters
        const selectedFacetIds = newQuery[this.fieldId] || [];
        this.selectedItems = this.items.filter((item) =>
          selectedFacetIds.includes(item.id)
        );
      },
    },
  },
  mounted() {
    this.id = this._uid; // eslint-disable-line
    if (this.fieldId === "country") this.loadLangList();
    if (this.getFacets.catalog) {
      const facetTitle = this.getAvailableFacets
        .find((field) => field.id === "catalog")
        ?.items.find((facet) => facet.id === this.getFacets.catalog[0])?.title;
      this.selectedCatalog = facetTitle;
    }
    if (this.fieldId == "publisher")
      this.filterPlaceholder = "Filter by most relevant Publishers";
    if (this.fieldId == "publisher" && this.selectedCatalog)
      this.filterPlaceholder = `Filter by ${this.selectedCatalog}`;
  },
  beforeDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
.label {
  vertical-align: text-bottom;
  color: #3f3f3f;
}

.custom-checkbox input {
  display: none;
}

.custom-checkbox .checkmark {
  cursor: pointer;
  width: 20px;
  height: 20px;
  border: 1px solid #333;
  border-radius: 5px;
  display: inline-block;
  position: relative;
  margin-right: 5px;
}

.ecl-custom-checkbox .checkmark:hover {
  box-shadow: 0 0 0 20px #d0d9f5;
  clip-path: circle(90%);
}

.custom-checkbox input:checked + .checkmark {
  background-color: #0e47cb;
  border-color: #0e47cb;
  color: #fff;
}

.custom-checkbox input:checked + .checkmark::after {
  content: url("../../assets/img/ecl/check-lg.svg");
  font-size: 16px;
  color: white;
  position: absolute;
  left: 1px;
  top: 1px;
}

.dropdown {
  position: absolute;
  z-index: 999;
  // background: transparent !important;
}

.button-group {
  margin-top: 15px;
  border-top: 1px solid #dadada;
}

.button-group .btn {
  width: -webkit-fill-available;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 15px;
  height: 44px;
}

.button-group .btn:hover {
  color: #fff;
  background-color: #3e6cd5 !important;
  border-color: #3e6cd5 !important;
}

.no-results {
  color: #242424;
  padding: 20px;
  min-height: 50px;
  text-align: center;
}

.ecl-text-input {
  width: -webkit-fill-available;
  margin: 10px 10px 10px 10px;
  border-radius: 5px;
  border: 1px solid #707070;
  box-shadow: 0 2px 4px rgba(9, 49, 142, 0.08) inset,
    0 0 10px rgba(9, 49, 142, 0.04) inset,
    0 4px 5px rgba(9, 49, 142, 0.04) inset,
    0 -4px 4px rgba(9, 49, 142, 0.04) inset;
}

.select-item {
  padding: 16px;
  // display: flex;

  &:hover {
    background-color: #ffffff;
  }

  background-color: #ffffff;
}

.highlighted-row {
  background-color: #e3e3e3;
}

.facet-container {
  padding: 0;
  font-family: Arial, sans-serif;
}

.dropdown {
  width: 100%;
  background: #f5f5f5;
  border: 1px solid #e3e3e3;
}

.dropdown-options {
  width: 100%;
  max-height: 300px;
  overflow: auto;
}

.border-top {
  border-top: 1px solid #dadada;
  margin-top: 5px;
}
</style>
